import React, { useState, useEffect } from "react";
import {
  Card,
  Button,
  Modal,
  Form,
  Table,
  InputGroup,
  FormControl,
} from "react-bootstrap";
import Select from "react-select";
import { toast } from "react-toastify";
import { FiEdit, FiUpload, FiDownload, FiPlus } from "react-icons/fi";
import { MdAddchart } from "react-icons/md";
import Pagination from "../Constant/Pagination";
import { MultiActionDropdown } from "../../components";
import { Spinner } from "react-bootstrap";
import { FaReadme } from "react-icons/fa";
import { FiMap } from "react-icons/fi";

import { Link } from "react-router-dom";
import Block from "../../components/Block/Block";
import {
  getAllAreas,
  createArea,
  updateArea,
  uploadAreaExcel,
  getAllLocations,
} from "../../services/SettingServices";
import { SearchDepartmentByLocationApi } from "../../services/SearchService";
import GlobalSearchableDropdown from "../Constant/SearchableDropdown";
import DebounceSmallSearchInput from "../Constant/DebounceSmallSearchInput";

const AreaSettings = () => {
  const [data, setData] = useState([]);
  const [locations, setLocations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [isLocationLoading, setIsLocationLoading] = useState(false);
  const [isDepartmentLoading, setIsDepartmentLoading] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState("");
  const [editLocation, setEditLocation] = useState("");
  const [statusFilter, setStatusFilter] = useState(null); // null = all
  const [formData, setFormData] = useState({
    name: "",
    department: "",
    is_active: true,
  });
  const [editData, setEditData] = useState(null);

  const pageSize = 10;

  const fetchAreas = async () => {
    setLoading(true);
    try {
      const res = await getAllAreas({
        page,
        page_size: pageSize,
        query: search,
        is_active: statusFilter,
      });
      setData(res.data.results || []);
      setCount(res.data.count || 0);
    } catch {
      toast.error("Failed to fetch areas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAreas();
  }, [page, search, statusFilter]);

  const fetchLocations = async () => {
    setIsLocationLoading(true);
    try {
      const res = await getAllLocations();
      setLocations(res.data.results || []);
    } catch {
      toast.error("Failed to fetch locations");
    } finally {
      setIsLocationLoading(false);
    }
  };

  const fetchDepartmentsByLocation = async (locId) => {
    setIsDepartmentLoading(true);
    try {
      const res = await SearchDepartmentByLocationApi(locId);
      // Ensure consistent format - map to {value, label} if not already
      const mapped = res.data.results.map((dept) => ({
        value: dept.value || dept.id, // Use whichever field is correct
        label: dept.label || dept.name, // Use whichever field is correct
      }));
      setDepartments(mapped);
    } catch {
      toast.error("Failed to fetch departments");
    } finally {
      setIsDepartmentLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.name.trim()) return toast.error("Area name required");
    try {
      await createArea(formData);
      fetchAreas();
      setFormData({ name: "", department: "", is_active: true });
      setShowModal(false);
      toast.success("Area created successfully");
    } catch {
      toast.error("Failed to create area");
    }
  };

  const handleUpdate = async () => {
    if (!editData?.name?.trim()) return toast.error("Area name required");
    try {
      await updateArea(editData.id, editData);
      fetchAreas();
      setShowEditModal(false);
      toast.success("Area updated successfully");
    } catch {
      toast.error("Failed to update area");
    }
  };
  const handleExport = async () => {
    try {
      const res = await getAllAreas({
        export: true,
        query: search,
        is_active: statusFilter,
      });
      const blob = new Blob([res.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "areas.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      toast.error("Export failed");
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      await uploadAreaExcel(file);
      fetchAreas();
      setShowUploadModal(false);
      toast.success("Upload successful");
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const locationOptions = locations.map((loc) => ({
    value: loc.id,
    label: loc.name,
  }));
  const departmentOptions = departments;

  return (
    <>
      <div className="card p-2 mb-2">
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-1">
          <div className="flex-grow-1 flex-md-grow-0">
            <h5 className="mb-0 d-flex align-items-center gap-2">
              <FiMap  size={18} className="text-primary" />
              Areas
            </h5>
          </div>
          <div className="d-flex gap-2 align-items-center flex-wrap">
            <DebounceSmallSearchInput
              handleSearchChange={(value) => setSearch(value)}
            />
            {/* Status Filter Dropdown */}
            <GlobalSearchableDropdown
              options={[
                { value: "true", label: "Active" },
                { value: "false", label: "Inactive" },
              ]}
              selected={
                statusFilter !== null
                  ? [
                      { value: "true", label: "Active" },
                      { value: "false", label: "Inactive" },
                    ].find((opt) => opt.value === statusFilter)
                  : null
              }
              onSelect={(selectedOption) => {
                setStatusFilter(selectedOption ? selectedOption.value : null);
                setPage(1); // Reset page on filter change
              }}
              placeholder="Status"
              isClearable={true}
            />

            <MultiActionDropdown
              variant="outline-primary"
              btnText="Add"
              btnIcon={<MdAddchart />}
              btnOnClick={() => setShowModal(true)}
              menuOptions={[
                {
                  label: "Add",
                  icon: <FiPlus />,
                  onClick: () => setShowModal(true),
                },
                {
                  label: "Import",
                  icon: <FiUpload />,
                  onClick: () => setShowUploadModal(true),
                },
                {
                  label: "Export",
                  icon: <FiDownload />,
                  onClick: handleExport,
                },
              ]}
            />
          </div>
        </div>
      </div>
      <Card>
        <Card.Body className="p-0">
          {loading ? (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ minHeight: 120 }}
            >
              <Spinner animation="border" size={35} color="#255d5c" />
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table fs-6 mb-0 align-middle">
              <thead className="table-light custom-thead">
                <tr>
                  <th>Name</th>
                  <th>Department</th>
                  <th>Location</th>
                  <th>Active</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {data.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="text-center py-3">
                      No data found
                    </td>
                  </tr>
                ) : (
                  data.map((row, idx) => (
                    <tr key={idx}>
                      <td>{row.name}</td>
                      <td>{row.department}</td>
                      <td>{row.location}</td>
                      <td>
                        <span
                          className={`badge ${
                            row.is_active
                              ? "text-bg-success-soft"
                              : "text-bg-danger-soft"
                          }`}
                        >
                          {row.is_active ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td>
                        <FiEdit
                          size={14}
                          className="text-primary me-2"
                          style={{ cursor: "pointer" }}
                          onClick={async () => {
                            setEditData({
                              ...row,
                              department: row.department_id || row.department,
                            });
                            setEditLocation(row.location_id);
                            await fetchLocations();
                            if (row.location_id) {
                              await fetchDepartmentsByLocation(row.location_id);
                            }
                            setShowEditModal(true);
                          }}
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              </table>
            </div>
          )}
          <div className="d-flex justify-content-end">
            <Pagination
              currentPage={page}
              recordsPage={pageSize}
              dataLength={count}
              handlePagination={(obj) => setPage(obj.selected + 1)}
              showPageInfo={false}
            />
          </div>
        </Card.Body>
      </Card>

      {/* Add Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Add Area</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-2">
              <Form.Label>Location</Form.Label>
              <Select
                options={locationOptions}
                value={
                  locationOptions.find(
                    (opt) => opt.value === selectedLocation
                  ) || null
                }
                onChange={(s) => {
                  setSelectedLocation(s?.value || "");
                  setFormData({ ...formData, department: "" });
                  if (s) fetchDepartmentsByLocation(s.value);
                }}
                onMenuOpen={fetchLocations}
                isClearable
                isLoading={isLocationLoading}
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Department</Form.Label>
              <Select
                options={departmentOptions}
                value={
                  departmentOptions.find(
                    (opt) => opt.value === formData.department
                  ) || null
                }
                onChange={(s) =>
                  setFormData({ ...formData, department: s?.value || "" })
                }
                isClearable
                isLoading={isDepartmentLoading}
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Area Name</Form.Label>
              <Form.Control
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
              />
            </Form.Group>
            <Form.Check
              label="Active"
              type="checkbox"
              checked={formData.is_active}
              onChange={(e) =>
                setFormData({ ...formData, is_active: e.target.checked })
              }
            />
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Close
          </Button>
          <Button variant="primary" onClick={handleCreate}>
            Save
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Area</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-2">
              <Form.Label>Location</Form.Label>
              <Select
                options={locationOptions}
                value={
                  locationOptions.find((opt) => opt.value === editLocation) ||
                  null
                }
                onChange={(s) => {
                  setEditLocation(s?.value || "");
                  setEditData({ ...editData, department: "" });
                  if (s) fetchDepartmentsByLocation(s.value);
                }}
                onMenuOpen={fetchLocations}
                isClearable
                isLoading={isLocationLoading}
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Department</Form.Label>
              <Select
                options={departmentOptions}
                value={departmentOptions.find(
                  (opt) => opt.value === editData?.department
                )}
                onChange={(s) =>
                  setEditData({ ...editData, department: s?.value || "" })
                }
                isClearable
                isLoading={isDepartmentLoading}
              />
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label>Area Name</Form.Label>
              <Form.Control
                value={editData?.name || ""}
                onChange={(e) =>
                  setEditData({ ...editData, name: e.target.value })
                }
              />
            </Form.Group>
            <Form.Check
              label="Active"
              type="checkbox"
              checked={editData?.is_active || false}
              onChange={(e) =>
                setEditData({ ...editData, is_active: e.target.checked })
              }
            />
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleUpdate}>
            Update
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Upload Modal */}
      <Modal show={showUploadModal} onHide={() => setShowUploadModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Upload Areas</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Excel File</Form.Label>
            <Form.Control
              type="file"
              accept=".xls,.xlsx"
              onChange={handleUpload}
              disabled={uploading}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowUploadModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AreaSettings;
